/**
 * LiveKit Meeting Adapter
 *
 * Wraps LiveKit SDK to implement the IMeetingAdapter interface.
 * This allows LiveKit-specific code to be isolated and swappable.
 */

import { useMemo, useEffect, useState, useRef } from 'react';
import {
  useRoomContext,
  useLocalParticipant,
  useRemoteParticipants,
} from '@livekit/components-react';
import { ConnectionState, Track, RemoteParticipant, TrackPublication, Participant, RemoteTrack, RemoteTrackPublication, LocalParticipant, Room } from 'livekit-client';

/**
 * Simple event emitter for LiveKit participants
 * Provides Dyte-compatible on/off/emit interface
 */
class ParticipantEventEmitter {
  private handlers = new Map<string, Set<Function>>();

  on(event: string, handler: Function) {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);
  }

  off(event: string, handler: Function) {
    this.handlers.get(event)?.delete(handler);
  }

  emit(event: string, payload: unknown) {
    this.handlers.get(event)?.forEach(h => {
      try {
        h(payload);
      } catch (e) {
        console.error('Error in event handler:', e);
      }
    });
  }

  clear() {
    this.handlers.clear();
  }
}
import { PRESET_TYPE } from 'configs/meeting';
import type {
  IMeetingAdapter,
  IMeetingParticipant,
  IParticipantsCollection,
  IClassState,
} from './MeetingAdapter';

/**
 * In-class audio/recorder bots: `agent-student-<id>` (one AI Buddy per child
 * during an evaluation) and `recorder-student-<id>` / `recorder-<classId>-<ts>`
 * (the per-child and class egress recorders). None of them set metadata this
 * adapter's fallback can read as anything but a student — `{type:'recorder'}`,
 * `{type:'oral-eval-recorder',...}` and the agent's empty metadata all miss
 * every branch below and land on the `STUDENT` default.
 *
 * That default then fed a real bug: `Participants/index.tsx` assigns grid
 * slots by trailing numeric id (`extractUserIdFromParticipantId`), which is
 * the same id for `student-<id>`, `agent-student-<id>` and
 * `recorder-student-<id>`. Treating the bot as a `STUDENT` let its slot logic
 * read the bot as "the real student reconnecting under a new session id",
 * delete the student's own slot, and hand it to the bot instead — so the
 * class-level recording showed a blank bot tile where the student had been,
 * for the length of their evaluation.
 *
 * Checked by identity, not metadata, and before any metadata guess: a bot's
 * self-reported type is exactly the thing a future bot could get wrong, and
 * identity is the one thing lms-backend and ai-tutor both mint under this
 * exact, three-repo contract (see `RECORDER_IDENTITY_PREFIX` in
 * lms-backend's `inclass-agents/recording.js` and ai-tutor's
 * `recorder_identity_for`). Exact prefixes, not a substring test — the same
 * trap this fixes.
 */
const AGENT_IDENTITY_PREFIX = 'agent-student-';
const RECORDER_IDENTITY_PREFIX = 'recorder-';

export const isInClassBotIdentity = (identity?: string | null): boolean =>
  typeof identity === 'string'
  && (identity.startsWith(AGENT_IDENTITY_PREFIX) || identity.startsWith(RECORDER_IDENTITY_PREFIX));

/**
 * Extract preset/role from LiveKit participant metadata
 */
export const getPresetFromMetadata = (metadata?: string): string => {
  if (!metadata) return PRESET_TYPE.STUDENT;

  try {
    const parsed = JSON.parse(metadata);
    // Check various possible metadata formats
    if (parsed.presetName) return parsed.presetName;
    if (parsed.preset) return parsed.preset;
    if (parsed.role === 'tutor' || parsed.type === 'tutor') return PRESET_TYPE.TUTOR;
    if (parsed.role === 'student' || parsed.type === 'student') return PRESET_TYPE.STUDENT;
    if (parsed.role === 'viewer' || parsed.type === 'viewer') return PRESET_TYPE.VIEWER;
    if (parsed.role === 'assistant' || parsed.type === 'viewer') return PRESET_TYPE.VIEWER;
  } catch (e) {
    // If metadata is not valid JSON, check if it's a simple string
    if (metadata.includes('tutor')) return PRESET_TYPE.TUTOR;
    if (metadata.includes('viewer')) return PRESET_TYPE.VIEWER;
  }

  return PRESET_TYPE.STUDENT;
};

/**
 * Extract MediaStreamTrack from TrackPublication
 */
const extractTrack = (publication?: TrackPublication): MediaStreamTrack | null => {
  if (!publication || !publication.track) return null;
  return publication.track.mediaStreamTrack || null;
};

/**
 * Convert LiveKit local participant to IMeetingParticipant
 */
const convertLocalParticipant = (
  localParticipant: LocalParticipant | null | undefined,
  room: Room | null | undefined,
): IMeetingParticipant | null => {
  if (!localParticipant) return null;

  const videoPublication = Array.from(localParticipant.videoTrackPublications.values()).find(
    (pub: TrackPublication) => pub.source === Track.Source.Camera,
  );

  const audioPublication = Array.from(localParticipant.audioTrackPublications.values()).find(
    (pub: TrackPublication) => pub.source === Track.Source.Microphone,
  );

  const screenSharePublication = Array.from(localParticipant.videoTrackPublications.values()).find(
    (pub: TrackPublication) => pub.source === Track.Source.ScreenShare,
  );

  const videoTrack = extractTrack(videoPublication);
  const audioTrack = extractTrack(audioPublication);
  const screenShareTrack = extractTrack(screenSharePublication);

  const presetName = getPresetFromMetadata(localParticipant.metadata);

  return {
    customParticipantId: localParticipant.identity,
    userId: localParticipant.identity,
    id: localParticipant.sid,
    name: localParticipant.name || localParticipant.identity,
    audioEnabled: localParticipant.isMicrophoneEnabled,
    videoEnabled: localParticipant.isCameraEnabled,
    screenShareEnabled: !!screenSharePublication,
    presetName,
    audioTrack,
    videoTrack,
    screenShareTrack,
    metadata: localParticipant.metadata,

    enableAudio: async () => {
      await localParticipant.setMicrophoneEnabled(true);
    },
    disableAudio: async () => {
      await localParticipant.setMicrophoneEnabled(false);
    },
    enableVideo: async () => {
      await localParticipant.setCameraEnabled(true);
    },
    disableVideo: async () => {
      await localParticipant.setCameraEnabled(false);
    },
    setDevice: async (device: MediaDeviceInfo) => {
      if (room) {
        await room.switchActiveDevice(device.kind as MediaDeviceKind, device.deviceId);
      }
    },
  };
};

/**
 * Convert LiveKit remote participant to IMeetingParticipant
 * @param participant - LiveKit RemoteParticipant
 * @param emitter - Optional event emitter for Dyte-compatible events
 */
const convertRemoteParticipant = (
  participant: RemoteParticipant,
  emitter?: ParticipantEventEmitter
): IMeetingParticipant => {
  const videoPublication = Array.from(participant.videoTrackPublications.values()).find(
    (pub) => pub.source === Track.Source.Camera,
  );

  const audioPublication = Array.from(participant.audioTrackPublications.values()).find(
    (pub) => pub.source === Track.Source.Microphone,
  );

  const screenSharePublication = Array.from(participant.videoTrackPublications.values()).find(
    (pub) => pub.source === Track.Source.ScreenShare,
  );

  const videoTrack = extractTrack(videoPublication);
  const audioTrack = extractTrack(audioPublication);
  const screenShareTrack = extractTrack(screenSharePublication);

  // Identity wins over whatever the bot's own metadata claims to be — see
  // isInClassBotIdentity.
  const presetName = isInClassBotIdentity(participant.identity)
    ? PRESET_TYPE.VIEWER
    : getPresetFromMetadata(participant.metadata);

  return {
    customParticipantId: participant.identity,
    userId: participant.identity,
    id: participant.sid,
    name: participant.name || participant.identity,
    audioEnabled: participant.isMicrophoneEnabled,
    videoEnabled: participant.isCameraEnabled,
    screenShareEnabled: !!screenSharePublication,
    presetName,
    audioTrack,
    videoTrack,
    screenShareTrack,
    metadata: participant.metadata,

    // Remote participants can't control their own media
    enableAudio: async () => {},
    disableAudio: async () => {},
    enableVideo: async () => {},
    disableVideo: async () => {},
    setDevice: async () => {},

    // Event methods for Dyte compatibility (audioUpdate, videoUpdate events)
    on: emitter ? (event: string, handler: Function) => emitter.on(event, handler) : undefined,
    off: emitter ? (event: string, handler: Function) => emitter.off(event, handler) : undefined,
  };
};

/**
 * Hook that provides the LiveKit meeting adapter
 * Implements the IMeetingAdapter interface using LiveKit SDK
 */
export const useLiveKitMeetingAdapter = (): IMeetingAdapter => {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  const [roomJoined, setRoomJoined] = useState(false);

  // Cache participant objects to prevent unnecessary recreation
  const participantCacheRef = useRef<Map<string, IMeetingParticipant>>(new Map());

  // Store event emitters for each participant (keyed by participant.sid)
  const emittersRef = useRef<Map<string, ParticipantEventEmitter>>(new Map());

  // Get or create emitter for a participant
  const getOrCreateEmitter = (participantSid: string): ParticipantEventEmitter => {
    let emitter = emittersRef.current.get(participantSid);
    if (!emitter) {
      emitter = new ParticipantEventEmitter();
      emittersRef.current.set(participantSid, emitter);
    }
    return emitter;
  };

  // Track connection state
  useEffect(() => {
    if (!room) return;

    const handleConnectionStateChange = (state: ConnectionState) => {
      setRoomJoined(state === ConnectionState.Connected);
    };

    setRoomJoined(room.state === ConnectionState.Connected);
    room.on('connectionStateChanged', handleConnectionStateChange);

    return () => {
      room.off('connectionStateChanged', handleConnectionStateChange);
    };
  }, [room]);

  // Set up Room event listeners for audio track changes (Dyte-compatible audioUpdate events)
  useEffect(() => {
    if (!room) return;

    const handleTrackSubscribed = (
      track: RemoteTrack,
      _publication: RemoteTrackPublication,
      participant: RemoteParticipant
    ) => {
      const emitter = emittersRef.current.get(participant.sid);
      if (!emitter) return;

      if (track.kind === 'audio') {
        emitter.emit('audioUpdate', {
          audioEnabled: true,
          audioTrack: track.mediaStreamTrack,
        });
      }
    };

    const handleTrackUnsubscribed = (
      track: RemoteTrack,
      _publication: RemoteTrackPublication,
      participant: RemoteParticipant
    ) => {
      const emitter = emittersRef.current.get(participant.sid);
      if (!emitter) return;

      if (track.kind === 'audio') {
        emitter.emit('audioUpdate', {
          audioEnabled: false,
          audioTrack: null,
        });
      }
    };

    const handleTrackMuted = (
      publication: TrackPublication,
      participant: Participant
    ) => {
      const emitter = emittersRef.current.get(participant.sid);
      if (!emitter) return;

      if (publication.kind === 'audio') {
        emitter.emit('audioUpdate', {
          audioEnabled: false,
          audioTrack: null,
        });
      }
    };

    const handleTrackUnmuted = (
      publication: TrackPublication,
      participant: Participant
    ) => {
      const emitter = emittersRef.current.get(participant.sid);
      if (!emitter) return;

      if (publication.kind === 'audio') {
        emitter.emit('audioUpdate', {
          audioEnabled: true,
          audioTrack: publication.track?.mediaStreamTrack || null,
        });
      }
    };

    room.on('trackSubscribed', handleTrackSubscribed);
    room.on('trackUnsubscribed', handleTrackUnsubscribed);
    room.on('trackMuted', handleTrackMuted);
    room.on('trackUnmuted', handleTrackUnmuted);

    return () => {
      room.off('trackSubscribed', handleTrackSubscribed);
      room.off('trackUnsubscribed', handleTrackUnsubscribed);
      room.off('trackMuted', handleTrackMuted);
      room.off('trackUnmuted', handleTrackUnmuted);
    };
  }, [room]);

  // Convert local participant
  const self = useMemo((): IMeetingParticipant | null => {
    return convertLocalParticipant(localParticipant, room);
  }, [localParticipant, room]);

  // Convert all remote participants with proper memoization
  const allParticipants = useMemo((): IMeetingParticipant[] => {
    const currentParticipantIds = new Set<string>();
    const result: IMeetingParticipant[] = [];

    remoteParticipants.forEach((participant) => {
      const participantId = participant.sid;
      currentParticipantIds.add(participantId);

      // Get or create emitter for this participant
      const emitter = getOrCreateEmitter(participantId);

      // Get cached participant
      const cached = participantCacheRef.current.get(participantId);

      // Check if we need to update the cached participant
      const needsUpdate = !cached ||
        cached.audioEnabled !== participant.isMicrophoneEnabled ||
        cached.videoEnabled !== participant.isCameraEnabled ||
        cached.name !== (participant.name || participant.identity) ||
        cached.metadata !== participant.metadata ||
        extractTrack(Array.from(participant.videoTrackPublications.values()).find(
          (pub) => pub.source === Track.Source.Camera
        ))?.id !== cached.videoTrack?.id ||
        extractTrack(Array.from(participant.audioTrackPublications.values()).find(
          (pub) => pub.source === Track.Source.Microphone
        ))?.id !== cached.audioTrack?.id ||
        extractTrack(Array.from(participant.videoTrackPublications.values()).find(
          (pub) => pub.source === Track.Source.ScreenShare
        ))?.id !== cached.screenShareTrack?.id;

      if (needsUpdate) {
        // Create new participant object with emitter for event support
        const newParticipant = convertRemoteParticipant(participant, emitter);
        participantCacheRef.current.set(participantId, newParticipant);
        result.push(newParticipant);
      } else {
        // Reuse cached participant
        result.push(cached);
      }
    });

    // Clean up cache and emitters for participants who left
    const keysToDelete: string[] = [];
    participantCacheRef.current.forEach((_, key) => {
      if (!currentParticipantIds.has(key)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => {
      participantCacheRef.current.delete(key);
      // Clean up emitter for this participant
      const emitter = emittersRef.current.get(key);
      if (emitter) {
        emitter.clear();
        emittersRef.current.delete(key);
      }
    });

    return result;
  }, [remoteParticipants]);

  // Filter tutors
  const tutors = useMemo(
    () => allParticipants.filter((p) => p.presetName === PRESET_TYPE.TUTOR),
    [allParticipants],
  );

  // Filter students
  const students = useMemo(
    () => allParticipants.filter((p) => p.presetName === PRESET_TYPE.STUDENT),
    [allParticipants],
  );

  // Students without self
  const studentsWithOutSelf = useMemo(
    () => students.filter((p) => p.id !== self?.id),
    [students, self],
  );

  // Detect screen share
  const screenShareHost = useMemo(() => {
    // Check local participant first
    if (self?.screenShareEnabled) return self;

    // Check remote participants
    return allParticipants.find((p) => p.screenShareEnabled) || null;
  }, [self, allParticipants]);

  // Participants collection
  const participants: IParticipantsCollection = useMemo(
    () => ({
      toArray: () => allParticipants,
      size: allParticipants.length,
    }),
    [allParticipants],
  );

  // Class state (default to TEACH for recorder)
  const classState: IClassState = useMemo(
    () => ({
      type: 'teach',
      level: null,
    }),
    [],
  );

  // Socket user type
  const socketUser = self?.presetName === PRESET_TYPE.VIEWER ? PRESET_TYPE.VIEWER : 'recorder';

  // Create mock meeting object for compatibility
  const meeting = useMemo(
    () => ({
      self: self,
      participants: {
        joined: {
          toArray: () => allParticipants,
          size: allParticipants.length,
        },
      },
      plugins: {
        active: { toArray: () => [], size: 0 },
        all: { toArray: () => [], get: () => null },
      },
      meta: {
        roomJoined,
      },
    }),
    [self, allParticipants, roomJoined],
  );

  return useMemo(
    () => ({
      // Self
      self,
      selfId: self?.customParticipantId || '',
      selfName: self?.name || '',

      // Meeting state
      roomState: roomJoined ? 'joined' : 'idle',
      audioStatus: self?.audioEnabled || false,
      videoStatus: self?.videoEnabled || false,

      // Participants
      participants,

      // Derived lists
      tutors,
      students,
      studentsWithOutSelf,
      studentsCount: students.length,

      // Tutor info
      tutorName: tutors[0]?.name || '',
      tutorId: tutors[0]?.id || '',

      // Screen share
      screenShareHost,
      screenShareEnabled: !!screenShareHost,

      // Plugins (not supported in LiveKit, return empty arrays)
      activePlugins: [],
      allPlugins: [],

      // Class state
      classState,
      isTutorInMainRoom: tutors.length > 0,
      isHostPresent: tutors.length > 0,

      // User info
      loggedInUser: self?.presetName || '',
      socketUser,
      studentDetails: null,
      subscribedSubjects: [],
      studentGroup: [],
      studentName: self?.name || '',

      // Native objects
      meeting,
      room,
    }),
    [
      self,
      roomJoined,
      participants,
      tutors,
      students,
      studentsWithOutSelf,
      screenShareHost,
      classState,
      socketUser,
      meeting,
      room,
    ],
  );
};
